import { z } from "zod";
import { RichText } from "../RichText";
import { globalSectionSchema, backgroundStyleEnum, subHeadingLevel } from "../globalProps";
import { SectionShell } from "../SectionShell";

const featureItemSchema = z.object({
  icon: z.string().optional(),
  title: z.string().default("Feature"),
  body: z.string().default("Description of this feature."),
});

export const schema = globalSectionSchema.extend({
  heading: z.string().default("Why Choose Us"),
  subheading: z.string().optional(),
  items: z
    .array(featureItemSchema)
    .default([
      {
        icon: "01",
        title: "Personalized Approach",
        body: "Every engagement starts with understanding your unique situation. We tailor our process to fit your needs, not the other way around.",
      },
      {
        icon: "02",
        title: "Experienced Professionals",
        body: "Our team brings decades of combined experience across a wide range of practice areas and industries.",
      },
      {
        icon: "03",
        title: "Transparent Communication",
        body: "You will always know where things stand. We provide clear updates and plain-language explanations at every stage.",
      },
      {
        icon: "04",
        title: "Results That Matter",
        body: "We measure success by your outcomes. Our track record speaks to the quality of guidance we provide.",
      },
    ]),
  iconStyle: z.enum(["emoji", "number", "none"]).default("number"),
  backgroundStyle: backgroundStyleEnum.default("light"),
});

export type Props = z.infer<typeof schema>;
export const defaults: Props = schema.parse({});

export function Component(props: Props) {
  const result = schema.safeParse(props);
  const safe = result.success ? result.data : defaults;
  const SubH = subHeadingLevel(safe.headingLevel);

  if (safe.items.length === 0) return null;

  return (
    <SectionShell globals={safe} widthScale="narrow">
        {/* Section header */}
        <div className="text-center mb-12">
          <RichText
            as={safe.headingLevel}
            html={safe.heading}
            className="text-3xl @md:text-[2.5rem] font-bold mb-4"
            data-prop="heading"
          />
          {safe.subheading && (
            <RichText
              as="div"
              html={safe.subheading}
              className="text-lg opacity-80"
              data-prop="subheading"
            />
          )}
        </div>

        {/* Items */}
        <div className="divide-y divide-current/10">
          {safe.items.map((item, i) => (
            <div key={i} className="flex gap-5 py-6 first:pt-0 last:pb-0">
              {/* Icon/number badge */}
              {safe.iconStyle !== "none" && (
                <div className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center text-sm font-bold shrink-0">
                  {item.icon || String(i + 1)}
                </div>
              )}

              {/* Text content */}
              <div className="flex-1 min-w-0">
                <RichText
                  as={SubH}
                  html={item.title}
                  className="text-lg font-semibold mb-2"
                  data-prop={`items.${i}.title`}
                />
                <RichText
                  as="p"
                  html={item.body}
                  className="leading-relaxed opacity-80"
                  data-prop={`items.${i}.body`}
                />
              </div>
            </div>
          ))}
        </div>
    </SectionShell>
  );
}
